// Editar usuário — presentational behavior of the redesigned form
// (tabs, permission-group collapse, live counters, password strength meter,
// header name sync). All form wiring lives in manager--users; this controller
// only reflects state into the new layout.
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "avatar", "headName", "nameInput",
    "permTabCount", "repTabCount", "notifTabCount", "permCount",
    "expandLabel", "pwdBar", "pwdLabel"
  ];

  connect() {
    this.recount = this.recount.bind(this);
    // Recompute counters after any interaction inside the form — covers native
    // clicks and the programmatic checkbox changes done by manager--users.
    this.element.addEventListener("click", this.scheduleRecount.bind(this));
    this.element.addEventListener("change", this.scheduleRecount.bind(this));
    this.recount();
    this.refreshExpandLabel();
  }

  // ---- tabs ----
  switchTab(event) {
    const key = event.currentTarget.dataset.tab;
    this.element.querySelectorAll(".utab").forEach((tab) =>
      tab.classList.toggle("is-active", tab === event.currentTarget)
    );
    this.element.querySelectorAll(".tabpanel").forEach((panel) =>
      panel.classList.toggle("is-active", panel.dataset.panel === key)
    );
  }

  // ---- permission groups collapse ----
  toggleGroup(event) {
    event.currentTarget.closest(".category-group").classList.toggle("is-collapsed");
    this.refreshExpandLabel();
  }

  toggleAllGroups() {
    const groups = [...this.element.querySelectorAll(".category-group")];
    const anyOpen = groups.some((group) => !group.classList.contains("is-collapsed"));
    groups.forEach((group) => group.classList.toggle("is-collapsed", anyOpen));
    this.refreshExpandLabel();
  }

  refreshExpandLabel() {
    if (!this.hasExpandLabelTarget) return;
    const anyOpen = [...this.element.querySelectorAll(".category-group")].some(
      (group) => !group.classList.contains("is-collapsed")
    );
    this.expandLabelTarget.textContent = anyOpen ? "Recolher tudo" : "Expandir tudo";
  }

  // ---- counters ----
  scheduleRecount() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(this.recount);
  }

  recount() {
    const checkedActions = this.element.querySelectorAll(".permission-action:checked").length;
    if (this.hasPermCountTarget) this.permCountTarget.textContent = checkedActions;
    if (this.hasPermTabCountTarget) this.permTabCountTarget.textContent = checkedActions;

    this.element.querySelectorAll(".category-group").forEach((group) => {
      const total = group.querySelectorAll(".permission-action").length;
      const on = group.querySelectorAll(".permission-action:checked").length;
      const count = group.querySelector(".perm-group__count");
      if (count) count.innerHTML = `<b>${on}</b>/${total}`;
    });

    if (this.hasRepTabCountTarget) {
      this.repTabCountTarget.textContent =
        this.element.querySelectorAll(".report-row input:checked").length;
    }

    if (this.hasNotifTabCountTarget) {
      this.notifTabCountTarget.textContent =
        this.element.querySelectorAll(".nchk input:checked").length;
    }
  }

  // ---- header name sync ----
  syncName(event) {
    const value = event.target.value.trim();
    if (this.hasHeadNameTarget) this.headNameTarget.textContent = value || "Novo usuário";
    if (this.hasAvatarTarget) {
      const initials = value.split(/\s+/).map((word) => word[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
      this.avatarTarget.childNodes[0].nodeValue = initials || "–";
    }
  }

  // ---- password strength meter ----
  scorePassword(event) {
    if (!this.hasPwdBarTarget) return;
    const value = event.target.value;

    if (!value) {
      this.pwdBarTarget.style.width = "0";
      this.pwdBarTarget.style.background = "#b6b0a8";
      this.pwdLabelTarget.textContent = "—";
      this.pwdLabelTarget.style.color = "";
      return;
    }

    let score = 0;
    if (value.length >= 8) score++;
    if (value.length >= 12) score++;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++;
    if (/\d/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;
    score = Math.min(score, 4);

    const widths = [12, 35, 60, 82, 100];
    const colors = ["#b23a2a", "#c85a3e", "#c99a3e", "#7e9a4a", "#4a6b3e"];
    const labels = ["Muito fraca", "Fraca", "Razoável", "Boa", "Forte"];

    this.pwdBarTarget.style.width = `${widths[score]}%`;
    this.pwdBarTarget.style.background = colors[score];
    this.pwdLabelTarget.textContent = labels[score];
    this.pwdLabelTarget.style.color = colors[score];
  }
}
